/**
 * `/metal-resources/metal-market-intelligence`: the landing page of "The Gauge", Ryerson's Metal
 * Market Intelligence blog. Ryerson build only (`notFound` otherwise). The page body is
 * `components/Blog/Landing.tsx`: category tiles, the Cup O' Joe podcast banner, the article list
 * with Load More, the newsletter band and Material Movers.
 *
 * Build-time data, all from Contentful through the `src/contentful/` fetchers, each of which
 * throws and fails the build on error (web-architecture ADR-0033):
 * - `getContentfulBlogEntries("all")`: the Ryerson articles per language, newest first. Unlike
 *   `[slug].tsx`, this list is filtered by brand.
 * - `getContentfulBlogCategoryEntries()`: every category, in `ordinal` order.
 * - `getContentfulMaterialMovers()`: the Material Movers section
 *   (`components/Blog/MaterialMovers.tsx`).
 *
 * Category and article pages are built by `./[slug].tsx`; the podcast pages by `./podcast/`.
 * Pinned (render only) by `src/unit-test/MetalMarketIntelligence/MetalMarketIntelligence.spec.tsx`.
 */
import React from "react";
import { useApplication } from "@ryerson/frontend.application";
import MetaHead, { MetaContent } from "@components/Shared/model/MetaHead";
import Landing, { BlogLandingStaticContent } from "@components/Blog/Landing";
import { useHasMounted } from "@components/Hooks";
import { LanguageContent } from "@components/Shared/model/LanguageContent";
import { allContentfulHeaderCalls, HeaderPageProps } from "@ryerson/frontend.contentful";
import HeaderLayout from "@components/NextLayouts/HeaderLayout";
import { getHeaderData } from "@components/NextLayouts/nextUtilities";
import getContentfulMaterialMovers from "@contentful/material-movers/getContentfulMaterialMovers";
import { MaterialMoversProps } from "@components/Blog/MaterialMovers";
import { getContentfulBlogEntries } from "@contentful/blog/getContentfulBlogEntries";
import { TranslatedBlogCategoryEntries, TranslatedBlogEntries } from "@contentful/blog/types";
import { getContentfulBlogCategoryEntries } from "@contentful/blog/getContentfulBlogCategoryEntries";

/** The landing's SEO tags. The `fr` and `es` entries repeat the English. */
const MetaObject: LanguageContent<MetaContent> = {
	en: {
		title: "Metals Industry Market Intelligence - The Gauge - Ryerson",
		description: `The Gauge, metals industry market intelligence powered by Ryerson.`,
		url: `/metal-resources/metal-market-intelligence`,
		company: true,
	},
	fr: {
		title: "Metals Industry Market Intelligence - The Gauge - Ryerson",
		description: `The Gauge, metals industry market intelligence powered by Ryerson.`,
		url: `/metal-resources/metal-market-intelligence`,
		company: true,
	},
	es: {
		title: "Metals Industry Market Intelligence - The Gauge - Ryerson",
		description: `The Gauge, metals industry market intelligence powered by Ryerson.`,
		url: `/metal-resources/metal-market-intelligence`,
		company: true,
	},
};

/**
 * The hero's fixed copy: the "The Gauge" heading, the two-line "... Powered by" text and the
 * Ryerson logo beside it (`public/images/RyersonLogo.png`), and the Load More label.
 */
const StaticContent: LanguageContent<BlogLandingStaticContent> = {
	en: {
		title: "The Gauge",
		subTitle: ["Metal Market", "Intelligence Powered by"],
		subTitleLogo: "/images/RyersonLogo.png",
		loadMore: "Load More Articles",
	},
	fr: {
		title: "The Gauge",
		subTitle: ["Information sur le marché", "du métal Powered by"],
		subTitleLogo: "/images/RyersonLogo.png",
		loadMore: "Charger plus d'articles",
	},
	es: {
		title: "The Gauge",
		subTitle: ["Información del mercado", "de metales Powered by"],
		subTitleLogo: "/images/RyersonLogo.png",
		loadMore: "Cargar más artículos",
	},
};

/** Header and footer props plus the three Contentful lists described in the file header. */
export const getStaticProps = async () => {
	if (process.env.NEXT_PUBLIC_COMPANY !== "ryerson") {
		return { notFound: true }; // tells Next.js to skip page generation and return 404
	}

	const { props: headerFooterProps } = await allContentfulHeaderCalls();

	return {
		props: {
			...headerFooterProps,
			materialMovers: await getContentfulMaterialMovers(),
			blogEntries: await getContentfulBlogEntries("all"),
			blogCategoryEntries: await getContentfulBlogCategoryEntries(),
		},
	};
};

/**
 * The landing page. It renders nothing until mounted in the browser; `RootLayout`'s
 * `HasMounted` already holds every page back until then.
 */
const Page: React.FC<
	HeaderPageProps &
		MaterialMoversProps & {
			blogEntries: TranslatedBlogEntries;
			blogCategoryEntries: TranslatedBlogCategoryEntries;
		}
> = (props) => {
	const {
		localization: { language },
	} = useApplication();

	if (!useHasMounted()) return null;

	return (
		<HeaderLayout {...getHeaderData(props)}>
			<MetaHead content={MetaObject[language]} />
			<Landing
				content={StaticContent[language]}
				materialMovers={props.materialMovers}
				blogCategoryContent={props.blogCategoryEntries}
				blogContent={props.blogEntries}
			/>
		</HeaderLayout>
	);
};

export default Page;
